import { Link, useParams } from 'react-router';
import { useMethodOptions } from '../../../data/methodOptions';
import { canDelete, formatPeso } from '@tahi/core';
import { deleteExpense, restoreExpense } from '../../../data/commands/spending';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Money, ScreenHeader, showToast } from '../../../ui';
import { HistoryDisclosure } from '../../../ui/HistoryDisclosure';
import { copy, ENTRIES_BACK } from '../copy';
import { categoryName, dayHeading, formatWhen, methodLabel, sourceLabel } from '../format';
import { describeHistory } from '../historyText';
import { useAccounts, useBudgetSettings, useFunds } from '../../budget/hooks';
import { useCategories, useEntry, useEntryHistory, useGoBack, useToday } from '../hooks';
import { ACCENT_LINK, CARD, LABEL, SCREEN } from '../styles';

export function EntryDetailScreen() {
  const { id = '' } = useParams();
  const goBack = useGoBack('/m/spending/entries');
  const methods = useMethodOptions();
  const today = useToday();
  const entry = useEntry(id);
  const rows = useEntryHistory(id) ?? [];
  const categories = useCategories() ?? [];
  const funds = useFunds() ?? [];
  const accounts = useAccounts() ?? [];
  const budgetSettings = useBudgetSettings();
  const env = useCommandEnv();
  const tz = env.tz;

  if (entry === undefined || entry === null) {
    return (
      <main className={`${SCREEN} md:max-w-2xl`}>
        <ScreenHeader title={copy.detail.title} back={ENTRIES_BACK} />
        <p className="text-muted">{entry === undefined ? copy.loading : copy.detail.notFound}</p>
      </main>
    );
  }

  const byId = new Map(categories.map((c) => [c.id, c]));
  const nameOf = (cid: string) => categoryName(cid, byId);
  const fundName = (fid: string) =>
    funds.find((f) => f.id === fid)?.name ?? copy.detail.removedFund;
  const lines = describeHistory(rows, {
    categoryName: nameOf,
    sourceName: sourceLabel,
    fundName,
    accountName: (aid: string) =>
      accounts.find((a) => a.id === aid)?.name ?? copy.detail.removedAccount,
  });
  const amountText = formatPeso(entry.amountMinor);
  const deleted = entry.deletedAt !== null;
  const deletable = canDelete(entry);

  async function onDelete() {
    const r = await deleteExpense(env, id);
    if (!r.ok) return;
    showToast({
      message: copy.deleted(amountText),
      action: { label: copy.undo, onPress: () => void restoreExpense(env, id) },
    });
    goBack();
  }

  async function onRestore() {
    await restoreExpense(env, id);
    showToast({ message: copy.restored(amountText) });
  }

  return (
    <main className={`${SCREEN} md:max-w-2xl`}>
      <ScreenHeader
        title={entry.note ?? (entry.categoryId ? nameOf(entry.categoryId) : copy.detail.title)}
        back={ENTRIES_BACK}
      />
      <header className={CARD}>
        <p className={LABEL}>{dayHeading(entry.date, today)}</p>
        <p className="font-mono text-[40px] leading-tight tabular-nums text-fg">
          {entry.kind === 'refund' ? '−' : ''}
          <Money minor={entry.amountMinor} />
        </p>
        {deleted ? (
          <p role="status" className="mt-2 text-sm text-muted">
            {copy.detail.deletedBanner}
          </p>
        ) : null}
      </header>

      <dl className={`${CARD} grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm`}>
        <dt className="text-muted">{copy.detail.category}</dt>
        <dd className="text-fg">
          {entry.categoryId ? nameOf(entry.categoryId) : copy.detail.none}
        </dd>
        <dt className="text-muted">{copy.detail.note}</dt>
        <dd className="text-fg">{entry.note ?? copy.detail.none}</dd>
        <dt className="text-muted">{copy.detail.method}</dt>
        <dd className="text-fg">
          {methods.find((m) => m.value === entry.method)?.label ?? methodLabel(entry.method)}
        </dd>
        <dt className="text-muted">{copy.detail.date}</dt>
        <dd className="text-fg">{dayHeading(entry.date, today)}</dd>
        {entry.method?.startsWith('card:') ? (
          // v0.7.6: a card purchase is charged to the card, so "From" names the card.
          <>
            <dt className="text-muted">{copy.detail.account}</dt>
            <dd className="text-fg">
              {copy.detail.fromCard(
                methods.find((m) => m.value === entry.method)?.label ?? copy.detail.removedCard,
              )}
            </dd>
          </>
        ) : entry.accountId ? (
          <>
            <dt className="text-muted">{copy.detail.account}</dt>
            <dd className="text-fg">
              {accounts.find((a) => a.id === entry.accountId)?.name ?? copy.detail.removedAccount}
            </dd>
          </>
        ) : null}
        {entry.fundId && budgetSettings?.useFunds ? (
          <>
            <dt className="text-muted">{copy.detail.fund}</dt>
            <dd className="text-fg">{fundName(entry.fundId)}</dd>
          </>
        ) : null}
        <dt className="text-muted">{copy.detail.source}</dt>
        <dd className="text-fg">{sourceLabel(entry.sourceModule)}</dd>
      </dl>

      {!deletable ? (
        <p className="text-sm text-muted">
          {copy.detail.fromOtherModule(sourceLabel(entry.sourceModule))}
        </p>
      ) : null}

      <div className="flex gap-2">
        {deleted ? (
          <Button variant="primary" onClick={() => void onRestore()}>
            {copy.restore}
          </Button>
        ) : (
          <>
            <Link to={`/m/spending/entries/${id}/edit`} className={ACCENT_LINK}>
              {copy.edit}
            </Link>
            {deletable ? (
              <Button variant="destructive" onClick={() => void onDelete()}>
                {copy.delete}
              </Button>
            ) : null}
          </>
        )}
      </div>

      <HistoryDisclosure>
        <section aria-label={copy.detail.history} className={CARD}>
          <h2 className={`mb-3 ${LABEL}`}>{copy.detail.history}</h2>
          <ol className="flex flex-col gap-3 border-l border-divider pl-4">
            {lines.map((l) => (
              <li key={l.id}>
                <p className="text-xs text-muted">
                  <time dateTime={l.at}>{formatWhen(l.at, tz)}</time>
                </p>
                <p className="text-fg">{l.title}</p>
                {l.details.map((d) => (
                  <p key={d} className="text-sm text-muted">
                    {d}
                  </p>
                ))}
              </li>
            ))}
          </ol>
        </section>
      </HistoryDisclosure>
    </main>
  );
}
